import { useEffect, useRef } from 'react';

import type { PersonalisedVariantFallbackReason } from '@atlassian/edition-benefits-modal/services/personalised-variant';
import type { VariantId } from '@atlassian/edition-benefits-modal/types';

import { useOperationalAnalytics } from '../../../analytics/use-operational-analytics';
import { useEditionAwareness } from '../../../services/edition-awareness-context';

export function useVariantFallbackAnalytics({
	variantId,
	isResolved,
	fallbackReason,
}: {
	fallbackReason: PersonalisedVariantFallbackReason;
	isResolved: boolean;
	variantId: VariantId;
}): void {
	const { product, edition } = useEditionAwareness();
	const fireOperationalEvent = useOperationalAnalytics({ product, edition });
	const reportedReason = useRef<PersonalisedVariantFallbackReason | null>(null);

	useEffect(() => {
		// `pending` is the pre-resolution state, not an outcome. Reporting it
		// would double-count every impression.
		if (!isResolved || reportedReason.current === fallbackReason) {
			return;
		}

		reportedReason.current = fallbackReason;

		fireOperationalEvent({
			action: 'resolved',
			actionSubject: 'editionAwarenessModalVariant',
			actionSubjectId: 'jsmPersonalisedPremiumUpsellModal',
			attributes: {
				variantId,
				fallbackReason,
				isPersonalised: fallbackReason === 'none',
			},
		});
	}, [isResolved, fallbackReason, variantId, fireOperationalEvent]);
}
